import React, { useState, useEffect, useCallback, useContext } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBell,
  faExclamationTriangle,
  faCheck,
  faSpinner,
  faRotateRight
} from '@fortawesome/free-solid-svg-icons';
import useToken from 'hooks/useToken';
import { useToast } from 'context/providers/ToastProvider';
import AppContext from 'context/Context';
import { getToken } from 'firebase/messaging';
import { messaging } from '../../../firebase/firebaseConfig';
import { getBannerTheme } from './notificationBannerStyles';

const NotificationPermissionBanner = () => {
  const { config: { isDark } = {} } = useContext(AppContext);
  const { token } = useToken();
  const { showSuccessToast, showErrorToast } = useToast();
  const [permission, setPermission] = useState(() => {
    if (typeof window !== 'undefined' && 'Notification' in window) {
      return Notification.permission;
    }
    return 'unsupported';
  });
  const [isActivating, setIsActivating] = useState(false);

  const registerFirebaseToken = useCallback(async () => {
    try {
      const tokenFirebase = await getToken(messaging, {
        vapidKey: 'BDgxI0yryiAvMlu9vmMDZibB-bmaSt5pj1Vrun_IsGWmzS3h5ubI69nRxId9HteusWK3d1cA1_Y1tFMdV4UjUkE'
      });
      if (tokenFirebase && token) {
        await fetch('/api/account/GuardarToken', {
          method: 'POST',
          headers: {
            Authorization: 'Bearer ' + token,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ tokenFireBase: tokenFirebase })
        });
      }
      return tokenFirebase;
    } catch (err) {
      console.warn('Error registrando token Firebase en banner:', err);
      return null;
    }
  }, [token]);

  // Verificar estado del permiso periódicamente y cuando el usuario enfoca la pestaña
  useEffect(() => {
    const checkState = async () => {
      if (typeof window !== 'undefined' && 'Notification' in window) {
        const current = Notification.permission;
        setPermission(current);
        if (current === 'granted') {
          await registerFirebaseToken();
        }
      }
    };

    checkState();
    window.addEventListener('focus', checkState);
    return () => window.removeEventListener('focus', checkState);
  }, [registerFirebaseToken]);

  // Si ya están concedidas o el navegador no soporta notificaciones, no se muestra
  if (permission === 'granted' || permission === 'unsupported') {
    return null;
  }

  const handleRequestPermission = async () => {
    if (!('Notification' in window)) return;
    setIsActivating(true);
    try {
      const res = await Notification.requestPermission();
      setPermission(res);
      if (res === 'granted') {
        const fcmToken = await registerFirebaseToken();
        if (fcmToken && showSuccessToast) {
          showSuccessToast('Notificaciones activadas', 'Recibirás avisos de nuevos mensajes y tickets en tu escritorio.');
        }
      } else if (res === 'denied') {
        if (showErrorToast) {
          showErrorToast('Permiso denegado', 'Las notificaciones fueron bloqueadas. Habilítalas en el icono del candado en la barra de direcciones.');
        }
      }
    } catch (err) {
      if (showErrorToast) {
        showErrorToast('Error', err.message || 'No se pudo solicitar el permiso.');
      }
    } finally {
      setIsActivating(false);
    }
  };

  const handleVerify = async () => {
    setIsActivating(true);
    if ('Notification' in window) {
      const current = Notification.permission;
      setPermission(current);
      if (current === 'granted') {
        await registerFirebaseToken();
        if (showSuccessToast) {
          showSuccessToast('¡Listo!', 'Permisos de notificación activados correctamente.');
        }
      } else {
        if (showErrorToast) {
          showErrorToast(
            'Aún bloqueadas',
            'Sigue los pasos indicados para cambiar Notificaciones a "Permitir" en la configuración de la URL y vuelve a hacer clic aquí.'
          );
        }
      }
    }
    setIsActivating(false);
  };

  const isDenied = permission === 'denied';
  const theme = getBannerTheme(isDark, isDenied);

  return (
    <div
      className="mb-3 p-3 rounded-3 border shadow-none"
      style={{
        backgroundColor: theme.bannerBg,
        borderColor: theme.bannerBorder,
        borderLeft: theme.borderLeft
      }}
    >
      <div className="d-flex align-items-start gap-2 gap-sm-3">
        <div
          className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
          style={{
            width: '38px',
            height: '38px',
            minWidth: '38px',
            backgroundColor: theme.iconBg,
            color: theme.iconColor,
            fontSize: '1.05rem',
            marginTop: '2px'
          }}
        >
          <FontAwesomeIcon icon={isDenied ? faExclamationTriangle : faBell} />
        </div>

        <div className="flex-grow-1 min-w-0">
          <div className="d-flex align-items-center flex-wrap gap-2 mb-1">
            <h6
              className="mb-0 fw-bold"
              style={{
                color: theme.titleColor,
                fontSize: '0.92rem',
                lineHeight: '1.3'
              }}
            >
              {isDenied
                ? 'Notificaciones bloqueadas en el navegador'
                : 'Activa las notificaciones de soporte'}
            </h6>
            <span
              className="badge rounded-pill"
              style={{
                backgroundColor: theme.badgeBg,
                color: theme.badgeColor,
                border: `1px solid ${theme.badgeBorder}`,
                fontSize: '0.68rem',
                fontWeight: 600,
                padding: '0.25em 0.65em'
              }}
            >
              Requerido
            </span>
          </div>

          {isDenied ? (
            <div className="small mb-2" style={{ color: theme.textColor, fontSize: '0.83rem', lineHeight: '1.45' }}>
              <p className="mb-1 fw-medium" style={{ color: theme.textColor }}>
                Tu navegador tiene bloqueadas las notificaciones para este sitio. Para recibir alertas en tiempo real de WhatsApp:
              </p>
              <ol className="ps-3 mb-0" style={{ color: theme.textColor }}>
                <li>Haz clic en el icono de <strong>Ajustes / Candado</strong> a la izquierda de la dirección en la barra de tu navegador.</li>
                <li>En la opción <strong>Notificaciones</strong>, cambia de <em>Bloquear</em> a <strong>Permitir</strong>.</li>
                <li>Una vez permitido, haz clic en el botón <strong>Verificar y Activar</strong> de abajo.</li>
              </ol>
            </div>
          ) : (
            <p
              className="small mb-2"
              style={{
                color: theme.textColor,
                fontSize: '0.83rem',
                lineHeight: '1.4'
              }}
            >
              Para recibir avisos en tiempo real en tu escritorio cuando un cliente escriba por WhatsApp o se te asigne un ticket, es necesario activar las notificaciones.
            </p>
          )}

          <div className="d-flex align-items-center gap-2 mt-2 pt-1">
            {isDenied ? (
              <button
                type="button"
                onClick={handleVerify}
                disabled={isActivating}
                className="btn btn-sm btn-warning d-inline-flex align-items-center gap-2 shadow-xs fw-semibold px-3 py-1 text-dark"
                style={{ fontSize: '0.82rem', borderRadius: '6px' }}
              >
                <FontAwesomeIcon icon={isActivating ? faSpinner : faRotateRight} spin={isActivating} />
                <span>{isActivating ? 'Verificando...' : 'Verificar y Activar'}</span>
              </button>
            ) : (
              <button
                type="button"
                onClick={handleRequestPermission}
                disabled={isActivating}
                className="btn btn-sm btn-primary d-inline-flex align-items-center gap-2 shadow-xs fw-semibold px-3 py-1"
                style={{ fontSize: '0.82rem', borderRadius: '6px' }}
              >
                <FontAwesomeIcon icon={isActivating ? faSpinner : faCheck} spin={isActivating} />
                <span>{isActivating ? 'Activando...' : 'Activar Notificaciones'}</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationPermissionBanner;
